import { NOTIFICATION_MESSAGES } from '../../../utils/notification-messages';
import { toastWarn } from '../../../utils/toasts';
import { dateFormat, estTimeZoneFormat, isEditAllowed } from './functions';

export const DISTRIBUTION_LIST_TABLE_HEADERS = [
   {
      field: 'dgNumber',
      headerName: 'DG',
      headerTooltip: 'Distribution Group',
   },
   {
      field: 'createdAt',
      headerName: 'CREATED',
      headerTooltip: 'Created',
      valueFormatter: ({ value }) => dateFormat(value),
   },
   {
      field: 'lastUpdatedAt',
      headerName: 'LAST UPDATED',
      headerTooltip: 'Last Update Date',
      valueFormatter: ({ value }) => dateFormat(value),
   },
   {
      field: 'distributedAt',
      headerName: 'DIST DT',
      headerTooltip: 'Distributed Date',
      valueFormatter: ({ value }) => dateFormat(value),
   },
   {
      field: 'description',
      headerName: 'DESCRIPTION',
      headerTooltip: 'Description',
      editable: ({ data }) => isEditAllowed(data?.status),
      tooltipField: 'description',
      valueSetter: ({ newValue, data }) =>
         (data.description = newValue?.toUpperCase()),
   },
   {
      field: 'user',
      headerName: 'CR USER',
      headerTooltip: 'User',
   },
   {
      field: 'totalFare',
      headerName: 'FARE',
      headerTooltip: 'Total Fare',
   },
   {
      field: 'intDistributionTimeStamp',
      headerName: 'INT DIST DT',
      headerTooltip: 'Int Distribution TimeStamp',
      editable: ({ data }) => isEditAllowed(data?.status),
      valueFormatter: ({ value }) => dateFormat(value),
      valueGetter: ({ data }) => dateFormat(data?.intDistributionTimeStamp),
      valueSetter: ({ newValue, data }) =>
         (data.intDistributionTimeStamp = newValue?.toUpperCase()),
   },
   {
      field: 'exclusiveAccessIndicator',
      headerName: 'EX IND',
      headerTooltip: 'Exclusive Access Indicator',
      editable: ({ data }) => isEditAllowed(data?.status),
      valueSetter: ({ newValue, data }) => {
         const alphaRegex = /^[a-zA-Z]+$/;

         if (!alphaRegex.test(newValue) || newValue.length !== 1) {
            toastWarn(NOTIFICATION_MESSAGES.ONE_CHARACTER_ONLY);
            return false;
         }

         data.exclusiveAccessIndicator = newValue.toUpperCase();
         return true;
      },
   },
   {
      field: 'releaseIndicator',
      headerName: 'REL IND',
      headerTooltip: 'Release Indicator',
      editable: ({ data }) => isEditAllowed(data?.status),
      valueSetter: ({ newValue, data }) => {
         const alphaRegex = /^[a-zA-Z]+$/;

         if (!alphaRegex.test(newValue) || newValue.length !== 1) {
            toastWarn(NOTIFICATION_MESSAGES.ONE_CHARACTER_ONLY);
            return false;
         }

         data.releaseIndicator = newValue.toUpperCase();
         return true;
      },
   },
   {
      field: 'ignoreErrorsIndicator',
      headerName: 'IGN ERR',
      headerTooltip: 'Ignore Errors Indicator',
      editable: ({ data }) => isEditAllowed(data?.status),
      valueSetter: ({ newValue, data }) => {
         const alphaRegex = /^[a-zA-Z]+$/;

         if (!alphaRegex.test(newValue) || newValue.length !== 1) {
            toastWarn(NOTIFICATION_MESSAGES.ONE_CHARACTER_ONLY);
            return false;
         }

         data.ignoreErrorsIndicator = newValue.toUpperCase();
         return true;
      },
   },
   {
      field: 'lowFareCheckIndicator',
      headerName: 'LFC',
      headerTooltip: 'Low Fare Check Indicator',
      editable: ({ data }) => isEditAllowed(data?.status),
      valueSetter: ({ newValue, data }) => {
         const alphaRegex = /^[a-zA-Z]+$/;

         if (!alphaRegex.test(newValue) || newValue.length !== 1) {
            toastWarn(NOTIFICATION_MESSAGES.ONE_CHARACTER_ONLY);
            return false;
         }

         data.lowFareCheckIndicator = newValue.toUpperCase();
         return true;
      },
   },
   {
      field: 'taxIndicator',
      headerName: 'TAX IND',
      headerTooltip: 'Tax Indicator',
      editable: ({ data }) => isEditAllowed(data?.status),
      valueSetter: ({ newValue, data }) => {
         const alphaRegex = /^[a-zA-Z]+$/;

         if (!alphaRegex.test(newValue) || newValue.length !== 1) {
            toastWarn(NOTIFICATION_MESSAGES.ONE_CHARACTER_ONLY);
            return false;
         }

         data.taxIndicator = newValue.toUpperCase();
         return true;
      },
   },
   {
      field: 'justification',
      headerName: 'JUSTIFICATION',
      headerTooltip: 'Justification',
      editable: ({ data }) => isEditAllowed(data?.status),
      valueSetter: ({ newValue, data }) =>
         (data.justification = newValue?.toUpperCase()),
   },
   {
      field: 'email',
      headerName: 'EMAIL',
      headerTooltip: 'Email',
      width: 200,
      editable: ({ data }) => isEditAllowed(data?.status),
      tooltipField: 'email',
      valueSetter: ({ newValue, data }) => {
         const alphaRegex =
            /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/;

         if (!alphaRegex.test(newValue)) {
            toastWarn(NOTIFICATION_MESSAGES.INVALID_EMAIL);
            return false;
         }

         data.email = newValue.toUpperCase();
         return true;
      },
   },
   {
      field: 'status',
      headerName: 'STATUS',
      headerTooltip: 'Status',
   },
   {
      field: 'isDomesticOrInternational',
      headerName: 'D/I',
      headerTooltip: 'Is Domestic Or International',
   },
];

export const DISTRIBUTION_LIST_TABLE_LOG_HEADER = [
   {
      field: 'timeStamp',
      headerName: 'TIME STAMP',
      headerTooltip: 'Time Stamp',
      suppressSizeToFit: true,
      valueFormatter: ({ value }) => estTimeZoneFormat(value),
   },
   {
      field: 'user',
      headerName: 'CR USER',
      headerTooltip: 'User',
      suppressSizeToFit: true,
   },
   {
      field: 'action',
      headerName: 'ACTION',
      headerTooltip: 'Action',
      suppressSizeToFit: true,
   },
   {
      field: 'notes',
      headerName: 'NOTES',
      headerTooltip: 'Notes',
      suppressSizeToFit: true,
      tooltipField: 'notes',
   },
];
